import { ConfigDefinition } from '../../../../sharedComponents/customizableFields/types';
import { omitFields } from '../../../../core/utils/utils';
import { useIncidentExclusionContext } from '../Exclusions/incidentExclusionContext';
import { IncidentsConfiguration, SaveIncidentConfigPayload } from '../types';
import { useCallback, useState, useRef, useEffect } from 'react';
import { Button, ButtonStyles, Toggle } from '@silverfort/sf-components';
import IncidentExclusions from '../Exclusions/incidentExclusions';
import CustomizableFields from "../../../../sharedComponents/customizableFields/CustomizableFields";

interface ExclusionsContentProps {
    config: IncidentsConfiguration;
    setConfig: (configUpdater: (prev: IncidentsConfiguration | null) => IncidentsConfiguration) => void;
    onSave: (params: SaveIncidentConfigPayload) => void;
    isSaving: boolean;
    dirty: boolean;
    setDirty: (dirty: boolean) => void;
}

const IncidentConfigurationContent: React.FC<ExclusionsContentProps> = ({
    config,
    setConfig,
    onSave,
    isSaving,
    setDirty,
    dirty
}) => {
    const { getPendingChanges, hasPendingChanges, exclusionsLoading } = useIncidentExclusionContext();
    const { createdExclusions, updatedExclusions, deletedExclusionIds } = getPendingChanges();
    const [isValid, setIsValid] = useState<boolean>(true);
    const [applying, setApplying] = useState(false);
    const initialConfigRef = useRef(config);

    const setCustomFields = (customizableFields: Record<string, ConfigDefinition>) => {
        setConfig((prev) => ({
            ...(prev ?? config),
            customizableFields
        }));
    };

    const setIsActive = (isActive: boolean) => {
        setConfig((prev) => ({
            ...(prev ?? config),
            isActive
        }));
    };

    useEffect(() => {
        const initial = initialConfigRef.current;

        if (!initial) {
            setDirty(false);
            return;
        }

        if (
            initial.isActive !== config.isActive ||
            createdExclusions.length ||
            updatedExclusions.length ||
            deletedExclusionIds.length
        ) {
            setDirty(true);
            return;
        }

        const initialFields = initial.customizableFields || {};
        const currentFields = config.customizableFields || {};
        const initialValues = Object.entries(initialFields).map(([k, f]) => [k, f.currentValue]);
        const currentValues = Object.entries(currentFields).map(([k, f]) => [k, f.currentValue]);
        if (initialValues.length !== currentValues.length) {
            setDirty(true);
            return;
        }

        for (let i = 0; i < initialValues.length; i++) {
            const [k1, v1] = initialValues[i];
            const [k2, v2] = currentValues[i];
            if (k1 !== k2 || v1 !== v2) {
                setDirty(true);
                return;
            }
        }

        setDirty(false);
    }, [config, createdExclusions, updatedExclusions, deletedExclusionIds, setDirty]);

    const onApply = useCallback(() => {
        if (applying) return;
        setApplying(true);
        try {
            const { createdExclusions, updatedExclusions, deletedExclusionIds } = getPendingChanges();
            const formattedConfig = omitFields(config, [
                'customizableFields',
                'description',
                'name',
                'lastChanged'
            ]) as SaveIncidentConfigPayload;
            formattedConfig.customizableFieldsValues = Object.fromEntries(
                Object.entries(config.customizableFields ?? {}).map(([key, field]) => [key, field.currentValue])
            );
            formattedConfig.createdExclusions = createdExclusions;
            formattedConfig.updatedExclusions = updatedExclusions;
            formattedConfig.deletedExclusionIds = deletedExclusionIds;
            onSave(formattedConfig);
            initialConfigRef.current = config;
        } finally {
            setApplying(false);
        }
    }, [getPendingChanges, config, onSave, applying]);

    return (
        <div className="incident-config flex flex-col w-[950px] h-full font-sf bg-sf-white overflow-hidden">
            <div className="flex flex-col gap-4 w-full p-6 min-h-0 flex-1 overflow-y-auto">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-sf-gray-800">Incident status</span>
                    <Toggle checked={config.isActive} onChange={setIsActive} className="mt-1 ml-1" />
                </div>
                <div className="flex gap-4 w-full flex-grow">
                    {config.customizableFields && Object.keys(config.customizableFields).length > 0 && (
                        <div className="min-w-[350px]">
                            <CustomizableFields
                                customizableFields={config.customizableFields}
                                setCustomizableFields={setCustomFields}
                                setIsFormValid={setIsValid}></CustomizableFields>
                        </div>
                    )}
                    <IncidentExclusions incidentConfiguration={config} />
                </div>
            </div>
            <div className="flex justify-end border-t border-gray-200 px-6 py-4 shrink-0 bg-sf-white z-50">
                <Button
                    text={applying || isSaving ? 'Saving...' : 'Apply'}
                    buttonStyle={ButtonStyles.PrimarySolid}
                    disabled={applying || isSaving || !isValid || exclusionsLoading || (!hasPendingChanges && !dirty)}
                    onClick={onApply}
                />
            </div>
        </div>
    );
};

export default IncidentConfigurationContent;
